/**
 * APP_PREVIEW message validation + handler factory (Lane Q6).
 * Contract: eigenify-ai/docs/EIGENAPP-PREVIEW-PROTOCOL.md
 *
 * Payload caps (section 3, enforced on BOTH ends):
 *   bundleText           <= 5 MB (mirrors the deployer MAX_BUNDLE_SIZE)
 *   appJson (serialized) <= 1 MB
 *   seedData (serialized) <= 2 MB
 */
import {
  AppPreviewMessage,
  AppPreviewResultMessage,
  MessageHandler,
} from "../types";

export const PREVIEW_PROTOCOL_VERSION = 1;

// Mirrors eigenify-ai eigenapp-deployer.ts MAX_BUNDLE_SIZE = 5_000_000.
export const MAX_BUNDLE_TEXT_CHARS = 5_000_000;
export const MAX_APP_JSON_CHARS = 1_000_000;
export const MAX_SEED_DATA_CHARS = 2_000_000;

export interface ValidatedAppPreview {
  previewId: string;
  appJson: Record<string, unknown>;
  bundleText?: string;
  bundleUrl?: string;
  seedData?: Record<string, Array<Record<string, unknown>>>;
}

export type AppPreviewValidation =
  | { ok: true; value: ValidatedAppPreview }
  | { ok: false; previewId: string; error: string };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Pure validation of an APP_PREVIEW payload: protocol version, required
 * fields, and the payload caps. Returns a sanitized error message on
 * violation (sent back as APP_PREVIEW_RESULT status "error").
 */
export function validateAppPreviewMessage(message: unknown): AppPreviewValidation {
  const msg = message as Partial<AppPreviewMessage> | null | undefined;
  const previewId = typeof msg?.previewId === "string" ? msg.previewId : "";

  if (!isPlainObject(msg)) {
    return { ok: false, previewId, error: "APP_PREVIEW payload must be an object" };
  }

  if (msg.protocolVersion !== PREVIEW_PROTOCOL_VERSION) {
    return {
      ok: false,
      previewId,
      error: `Unsupported protocolVersion: expected ${PREVIEW_PROTOCOL_VERSION}, got ${String(
        msg.protocolVersion
      )}`,
    };
  }

  if (!previewId) {
    return { ok: false, previewId, error: "previewId must be a non-empty string" };
  }

  if (!isPlainObject(msg.appJson)) {
    return { ok: false, previewId, error: "appJson must be an object" };
  }

  let appJsonSize: number;
  try {
    appJsonSize = JSON.stringify(msg.appJson).length;
  } catch {
    return { ok: false, previewId, error: "appJson is not serializable" };
  }
  if (appJsonSize > MAX_APP_JSON_CHARS) {
    return {
      ok: false,
      previewId,
      error: `appJson exceeds the ${MAX_APP_JSON_CHARS / 1_000_000}MB cap (got ${appJsonSize} chars)`,
    };
  }

  const hasBundleText = typeof msg.bundleText === "string" && msg.bundleText.length > 0;
  const hasBundleUrl = typeof msg.bundleUrl === "string" && msg.bundleUrl.length > 0;

  if (!hasBundleText && !hasBundleUrl) {
    return { ok: false, previewId, error: "One of bundleText or bundleUrl is required" };
  }
  if (hasBundleText && hasBundleUrl) {
    return { ok: false, previewId, error: "Provide bundleText OR bundleUrl, not both" };
  }
  if (hasBundleText && msg.bundleText!.length > MAX_BUNDLE_TEXT_CHARS) {
    return {
      ok: false,
      previewId,
      error: `bundleText exceeds the ${MAX_BUNDLE_TEXT_CHARS / 1_000_000}MB cap (got ${
        msg.bundleText!.length
      } chars)`,
    };
  }

  let seedData: ValidatedAppPreview["seedData"];
  if (msg.seedData !== undefined) {
    if (!isPlainObject(msg.seedData)) {
      return { ok: false, previewId, error: "seedData must be an object of row arrays" };
    }
    for (const [tableId, rows] of Object.entries(msg.seedData)) {
      if (!Array.isArray(rows) || rows.some((row) => !isPlainObject(row))) {
        return {
          ok: false,
          previewId,
          error: `seedData["${tableId}"] must be an array of row objects`,
        };
      }
    }
    let seedSize: number;
    try {
      seedSize = JSON.stringify(msg.seedData).length;
    } catch {
      return { ok: false, previewId, error: "seedData is not serializable" };
    }
    if (seedSize > MAX_SEED_DATA_CHARS) {
      return {
        ok: false,
        previewId,
        error: `seedData exceeds the ${MAX_SEED_DATA_CHARS / 1_000_000}MB cap (got ${seedSize} chars)`,
      };
    }
    seedData = msg.seedData as ValidatedAppPreview["seedData"];
  }

  return {
    ok: true,
    value: {
      previewId,
      appJson: msg.appJson,
      bundleText: hasBundleText ? msg.bundleText : undefined,
      bundleUrl: hasBundleUrl ? msg.bundleUrl : undefined,
      seedData,
    },
  };
}

/** Sanitize an error for the APP_PREVIEW_RESULT message: message only, no stack. */
export function sanitizePreviewError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return message.slice(0, 500);
}

export type SendPreviewResult = (result: Omit<AppPreviewResultMessage, "type" | "protocolVersion">) => void;

export interface AppPreviewHandlerConfig {
  /** Render the validated preview; throw to report status "error". */
  onPreview: (payload: ValidatedAppPreview, sendResult: SendPreviewResult) => Promise<void> | void;
}

/**
 * Create the APP_PREVIEW MessageHandler (registered with PostMessageService
 * alongside RefreshResultHandler & co; origin validation happens in the
 * service BEFORE this handler runs).
 */
export const createAppPreviewHandler = (
  config: AppPreviewHandlerConfig
): MessageHandler<AppPreviewMessage> => {
  return async (message, event) => {
    // postMessage targetOrigin cannot be the literal "null" origin; that only
    // occurs for the development file:// harness allowance.
    const targetOrigin = event.origin && event.origin !== "null" ? event.origin : "*";
    const target = (event.source as WindowProxy | null) ?? window.parent;

    const sendResult: SendPreviewResult = (result) => {
      const response: AppPreviewResultMessage = {
        type: "APP_PREVIEW_RESULT",
        protocolVersion: PREVIEW_PROTOCOL_VERSION,
        ...result,
      };
      try {
        target.postMessage(response, targetOrigin);
      } catch (error) {
        console.error("[AppPreviewHandler] Failed to post APP_PREVIEW_RESULT:", error);
      }
    };

    const validation = validateAppPreviewMessage(message);
    if (validation.ok === false) {
      console.error("[AppPreviewHandler] Rejected APP_PREVIEW:", validation.error);
      sendResult({ previewId: validation.previewId, status: "error", error: validation.error });
      return;
    }

    try {
      await config.onPreview(validation.value, sendResult);
    } catch (error) {
      console.error("[AppPreviewHandler] Preview render failed:", error);
      sendResult({
        previewId: validation.value.previewId,
        status: "error",
        error: sanitizePreviewError(error),
      });
    }
  };
};
